import { UserRoles } from '@models/user';
import { useParams } from 'react-router-dom';
import { Box } from '@mui/material';
import invariant from 'tiny-invariant';

import Loading from '@components/Loading';
import { useQuote } from '@services/quotes';
import { BindCard } from '@components/bindCard/BindCard';
import { QuoteProposalCardRow } from './QuoteProposalCardRow';
import { formatCurrency } from '@utils/number';
import { useSearchQuoteRatings } from '@services/quoteRating/useSearchQuoteRatings';
import { searchLastSuccessfulRatingParams } from '@services/quoteRating/quoteRating.helpers';
import { QuoteType } from '@lio/rule-engine';
import { CodeName } from '@models/quoteItem';
import { getCoverageTypeText } from '@pages/BindQuote/getCoverageTypeText';
import { writingCompanyIds } from '@models/productOffering';
import { ProductOfferingId } from '@models/newQuote';
import { useQuoteStore } from '@store/Quote/QuoteStore';
import { shallow } from 'zustand/shallow';
export const documentGenerationAllowedRoles = [
  UserRoles.SystemAdmin,
  UserRoles.PortalAdmin,
  UserRoles.Underwriter,
  UserRoles.LIOContractor,
  UserRoles.CSR,
  UserRoles.Producer,
  UserRoles.JrOps,
];

const formatNumber = (value: number | undefined | null) =>
  value !== undefined && value !== null ? formatCurrency(value) : '-';

export const QuoteProposalCard = () => {
  const { quoteId } = useParams<{ quoteId: string }>();
  invariant(quoteId);
  const { quote } = useQuote(quoteId);
  const quoteNumber = quote?.policyNumber?.policyNumber ?? '-';
  const { structuredQuote } = useQuoteStore((state) => ({ structuredQuote: state.structuredQuote }), shallow);

  const { data: lastSuccessfulRating } = useSearchQuoteRatings({
    quoteId,
    enabled: true,
    params: searchLastSuccessfulRatingParams,
  });

  if (!quote || !structuredQuote) {
    return <Loading />;
  }

  const coverageText = structuredQuote ? getCoverageTypeText(structuredQuote, quote.writingCompany?.id) : '-';
  const estimatedPremium = quote?.termPremium ?? '-';

  const policyQuoteItem = structuredQuote.quoteItems.find(
    (quoteItem) => quoteItem.quoteItemType.codeName === CodeName.Policy,
  );
  const policyEffectiveDate =
    policyQuoteItem?.attributeTypes.PolicyEffectiveDate?.attribute?.valueOverride ??
    policyQuoteItem?.attributeTypes.PolicyEffectiveDate?.attribute?.value ??
    quote.effectiveOn;

  const isEndorsement = quote.quoteType === QuoteType.Endorsement;

  // Agent Commission for Special Events only
  const isSpecialEvent = quote.offering?.id === ProductOfferingId.SpecialEvent;
  const agentCommissionValue = isSpecialEvent
    ? ((policyQuoteItem?.attributeTypes.PolicyCommission?.attribute?.valueOverride ??
        policyQuoteItem?.attributeTypes.PolicyCommission?.attribute?.value) as number | undefined)
    : undefined;

  const quoteCardStyles = {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    '.quote-proposal-card-item': { marginBottom: '4px' },
    '.quote-proposal-card-item:last-child': { marginBottom: '-6px' },
  };

  const latestRating = lastSuccessfulRating?.pages[0].items[0];
  const isSpecialtyWritingCompany = quote.writingCompany?.id === writingCompanyIds.specialty;
  const isAdmitted = quote.writingCompany?.id === writingCompanyIds.admitted;

  const policyFee =
    !isAdmitted && typeof quote?.termTaxFeeSurcharge === 'number'
      ? formatCurrency(quote?.termTaxFeeSurcharge)
      : undefined;

  return (
    <Box id='review-proposal-card' data-testid='review-proposal-card'>
      <BindCard>
        {[
          {
            content:
              quote.multiPolicyQuoteDestinations?.length > 0 ? (
                <Box sx={quoteCardStyles}>
                  {quote.multiPolicyQuoteDestinations
                    .sort((a, b) => {
                      if (!a.insuranceLine) {
                        return -1;
                      }
                      if (!b.insuranceLine) {
                        return 1;
                      }
                      return a.insuranceLine.name.localeCompare(b.insuranceLine.name);
                    })
                    .map((q) => {
                      return (
                        <QuoteProposalCardRow
                          key={q.id}
                          policyCoverage={
                            q.insuranceLine?.name ??
                            (isSpecialtyWritingCompany ? 'Primary General Liability' : 'Package')
                          }
                          estimatedPremium={q.termPremium ?? '-'}
                          quoteNumber={q.policyNumber.policyNumber}
                          effectiveDate={quote.effectiveOn}
                          latestRatingResults={latestRating}
                          isEndorsement={isEndorsement}
                          policyFee={q.policyFee ? formatNumber(q.policyFee) : null}
                          taxFeeSurcharge={
                            isAdmitted && q.termTaxFeeSurcharge ? formatNumber(q.termTaxFeeSurcharge) : null
                          }
                          agentCommission={agentCommissionValue}
                        />
                      );
                    })}
                </Box>
              ) : (
                <Box sx={quoteCardStyles}>
                  <QuoteProposalCardRow
                    policyCoverage={coverageText}
                    estimatedPremium={estimatedPremium}
                    policyId={quote?.policyId}
                    quoteNumber={quoteNumber}
                    effectiveDate={isEndorsement ? quote.effectiveOn : (policyEffectiveDate as Date)}
                    policyFee={!isAdmitted ? policyFee : undefined}
                    taxFeeSurcharge={
                      isAdmitted && quote.termTaxFeeSurcharge ? formatCurrency(quote.termTaxFeeSurcharge) : null
                    }
                    latestRatingResults={latestRating}
                    isEndorsement={isEndorsement}
                    agentCommission={agentCommissionValue}
                  />
                </Box>
              ),
          },
        ]}
      </BindCard>
    </Box>
  );
};
